import { useEffect, useRef, useState } from "react";
import { api, type Block, type BlockContent, type Gallery, type Page } from "../../lib/api";
import { AssetPicker } from "../AssetPicker";

// Module-level cache: web_key → thumb_url, shared across all ImageFieldPreview instances
const _thumbCache: Record<string, string> = {};
let _thumbFetch: Promise<void> | null = null;
async function fetchThumbCache() {
  if (_thumbFetch) return _thumbFetch;
  _thumbFetch = api.listAllAssets().then(assets => {
    for (const a of assets) if (a.web_key) _thumbCache[a.web_key] = a.thumb_url;
  }).catch(() => {});
  return _thumbFetch;
}

function LinkPicker({
  label = "URL",
  value,
  onChange,
  placeholder = "/contact",
}: {
  label?: string;
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [tab, setTab] = useState<"pages" | "galleries" | "external">("pages");
  const [externalUrl, setExternalUrl] = useState("");
  const [loading, setLoading] = useState(false);

  function handleOpen() {
    const isExternal = value.startsWith("http://") || value.startsWith("https://");
    setTab(isExternal ? "external" : "pages");
    setExternalUrl(value);
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([api.listPages(), api.listGalleries()])
      .then(([p, g]) => { setPages(p); setGalleries(g); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  function selectPage(page: Page) {
    onChange(page.Slug === "home" ? "/" : `/${page.Slug}`);
    setOpen(false);
  }

  function selectGallery(gallery: Gallery) {
    onChange(`/galleries/${gallery.Slug}`);
    setOpen(false);
  }

  return (
    <div className="form-group" style={{ position: "relative" }}>
      <label>{label}</label>
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
        <div
          style={{
            flex: 1,
            padding: "0.45rem 0.6rem",
            fontSize: "0.875rem",
            border: "1px solid var(--border)",
            borderRadius: 8,
            background: "var(--bg)",
            color: value ? "var(--text)" : "var(--muted)",
            fontFamily: "monospace",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "default",
          }}
          title={value || placeholder}
        >
          {value || <span style={{ opacity: 0.5 }}>{placeholder}</span>}
        </div>
        <button
          type="button"
          className="btn-ghost"
          onClick={handleOpen}
          style={{ fontSize: "0.8rem", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Browse
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: "1rem", lineHeight: 1, padding: "0 0.1rem", flexShrink: 0 }}
            title="Clear"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 199 }}
            aria-hidden="true"
          />
          <div style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 200,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            marginTop: 4,
            overflow: "hidden",
          }}>
            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
              {(["pages", "galleries", "external"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  style={{
                    flex: 1,
                    padding: "0.5rem 0.25rem",
                    fontSize: "0.78rem",
                    fontWeight: tab === t ? 600 : 400,
                    background: "transparent",
                    border: "none",
                    borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
                    color: tab === t ? "var(--accent)" : "var(--muted)",
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {t}
                </button>
              ))}
            </div>

            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {loading && (
                <div style={{ padding: "0.75rem 1rem", color: "var(--muted)", fontSize: "0.85rem" }}>Loading…</div>
              )}

              {!loading && tab === "pages" && (
                <div>
                  {pages.map((page) => (
                    <button
                      key={page.ID}
                      type="button"
                      onClick={() => selectPage(page)}
                      style={{
                        display: "flex", alignItems: "center", gap: "0.5rem",
                        width: "100%", padding: "0.5rem 0.75rem",
                        background: "transparent", border: "none",
                        cursor: "pointer", textAlign: "left",
                        fontSize: "0.875rem", color: "var(--text)",
                      }}
                    >
                      <span style={{ flex: 1, fontWeight: 500 }}>{page.Title}</span>
                      <code style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                        {page.Slug === "home" ? "/" : `/${page.Slug}`}
                      </code>
                    </button>
                  ))}
                  {pages.length === 0 && (
                    <div style={{ padding: "0.75rem", fontSize: "0.85rem", color: "var(--muted)" }}>No pages found</div>
                  )}
                </div>
              )}

              {!loading && tab === "galleries" && (
                <div>
                  {galleries.map((gallery) => (
                    <button
                      key={gallery.ID}
                      type="button"
                      onClick={() => selectGallery(gallery)}
                      style={{
                        display: "flex", alignItems: "center", gap: "0.5rem",
                        width: "100%", padding: "0.5rem 0.75rem",
                        background: "transparent", border: "none",
                        cursor: "pointer", textAlign: "left",
                        fontSize: "0.875rem", color: "var(--text)",
                      }}
                    >
                      <span style={{ flex: 1, fontWeight: 500 }}>{gallery.Title}</span>
                      <code style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                        /galleries/{gallery.Slug}
                      </code>
                    </button>
                  ))}
                  {galleries.length === 0 && (
                    <div style={{ padding: "0.75rem", fontSize: "0.85rem", color: "var(--muted)" }}>No public galleries yet</div>
                  )}
                </div>
              )}

              {!loading && tab === "external" && (
                <div style={{ padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <input
                    autoFocus
                    type="url"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="https://example.com"
                    style={{ fontSize: "0.875rem" }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); onChange(externalUrl.trim()); setOpen(false); }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => { onChange(externalUrl.trim()); setOpen(false); }}
                    style={{ alignSelf: "flex-start", fontSize: "0.85rem" }}
                  >
                    Use this URL
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AssetField({
  label,
  value,
  onOpen,
  onClear,
}: {
  label: string;
  value: string | undefined;
  onOpen: () => void;
  onClear: () => void;
}) {
  const [thumbUrl, setThumbUrl] = useState(() => (value ? _thumbCache[value] ?? "" : ""));
  const prevValue = useRef(value);

  useEffect(() => {
    if (!value) { setThumbUrl(""); return; }
    if (_thumbCache[value]) { setThumbUrl(_thumbCache[value]); return; }
    fetchThumbCache().then(() => {
      if (value) setThumbUrl(_thumbCache[value] ?? "");
    });
    prevValue.current = value;
  }, [value]);

  return (
    <div className="form-group">
      <label>{label}</label>
      <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap" }}>
        {value && thumbUrl && (
          <img
            src={thumbUrl}
            alt=""
            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0, border: "1px solid var(--border)" }}
          />
        )}
        <button type="button" className="btn-ghost" onClick={onOpen} style={{ fontSize: ".875rem" }}>
          {value ? "Change image" : "+ Choose image"}
        </button>
        {value && (
          <button
            type="button"
            onClick={onClear}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: "1rem", lineHeight: 1, padding: "0 .2rem" }}
            title="Clear image"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

function HeroEditor({ content, onChange, openPicker }: { content: BlockContent; onChange: (c: BlockContent) => void; openPicker: (field: keyof BlockContent) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="form-group">
        <label>Heading</label>
        <input value={content.heading ?? ""} onChange={(e) => onChange({ ...content, heading: e.target.value })} placeholder="Your headline" />
      </div>
      <div className="form-group">
        <label>Subtext</label>
        <input value={content.subtext ?? ""} onChange={(e) => onChange({ ...content, subtext: e.target.value })} placeholder="Supporting line" />
      </div>
      <AssetField
        label="Background image"
        value={content.image_asset_id}
        onOpen={() => openPicker("image_asset_id")}
        onClear={() => onChange({ ...content, image_asset_id: "" })}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem" }}>
        <div className="form-group">
          <label>CTA label</label>
          <input value={content.cta_label ?? ""} onChange={(e) => onChange({ ...content, cta_label: e.target.value })} placeholder="Get in touch" />
        </div>
        <LinkPicker label="CTA URL" value={content.cta_url ?? ""} onChange={(url) => onChange({ ...content, cta_url: url })} placeholder="/contact" />
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={(e) => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #ffffff or var(--text)" />
      </div>
    </div>
  );
}

function TextEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  // Use a simple textarea for now; the MDEditor package needs special
  // Dark mode styling uses CSS variables; a dedicated MDEditor wrapper can be added later if needed.
  return (
    <div className="form-group">
      <label>Markdown content</label>
      <textarea
        rows={10}
        value={content.markdown ?? ""}
        onChange={(e) => onChange({ ...content, markdown: e.target.value })}
        placeholder="Write in markdown…"
        style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
      />
      <small style={{ color: "var(--muted)" }}>Markdown supported (bold, italic, headings, links, lists)</small>
      <div style={{ marginTop: "0.75rem" }}>
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={(e) => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function ImageEditor({ content, onChange, openPicker }: { content: BlockContent; onChange: (c: BlockContent) => void; openPicker: (field: keyof BlockContent) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <AssetField
        label="Image"
        value={content.asset_id}
        onOpen={() => openPicker("asset_id")}
        onClear={() => onChange({ ...content, asset_id: "" })}
      />
      <div className="form-group">
        <label>Caption</label>
        <input value={content.caption ?? ""} onChange={(e) => onChange({ ...content, caption: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Layout</label>
        <div style={{ display: "flex", gap: "1rem" }}>
          {(["full", "wide", "center"] as const).map((l) => (
            <label key={l} style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
              <input type="radio" name="layout" value={l} checked={(content.layout ?? "wide") === l} onChange={() => onChange({ ...content, layout: l })} style={{ width: "auto" }} />
              {l}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuoteEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="form-group">
        <label>Quote</label>
        <textarea rows={3} value={content.body ?? ""} onChange={(e) => onChange({ ...content, body: e.target.value })} placeholder="The words that moved you…" />
      </div>
      <div className="form-group">
        <label>Author</label>
        <input value={content.author ?? ""} onChange={(e) => onChange({ ...content, author: e.target.value })} placeholder="— Jane Doe" />
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={(e) => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function CalloutEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="form-group">
        <label>Icon / emoji</label>
        <input value={content.icon ?? ""} onChange={(e) => onChange({ ...content, icon: e.target.value })} placeholder="✨" style={{ width: "6rem" }} />
      </div>
      <div className="form-group">
        <label>Heading</label>
        <input value={content.heading ?? ""} onChange={(e) => onChange({ ...content, heading: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Body</label>
        <textarea rows={3} value={content.body ?? ""} onChange={(e) => onChange({ ...content, body: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={(e) => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function FAQEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  const items = content.faq_items ?? [];
  function addItem() {
    onChange({ ...content, faq_items: [...items, { ID: crypto.randomUUID(), Question: "", Answer: "", SortOrder: items.length }] });
  }
  function removeItem(idx: number) {
    onChange({ ...content, faq_items: items.filter((_, i) => i !== idx) });
  }
  function updateItem(idx: number, field: "Question" | "Answer", val: string) {
    onChange({ ...content, faq_items: items.map((item, i) => i === idx ? { ...item, [field]: val } : item) });
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {items.map((item, idx) => (
        <div key={item.ID} style={{ border: "1px solid var(--border)", borderRadius: "0.5rem", padding: "0.75rem" }}>
          <div className="form-group" style={{ marginBottom: "0.5rem" }}>
            <label>Question</label>
            <input value={item.Question} onChange={(e) => updateItem(idx, "Question", e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: "0.5rem" }}>
            <label>Answer</label>
            <textarea rows={2} value={item.Answer} onChange={(e) => updateItem(idx, "Answer", e.target.value)} />
          </div>
          <button type="button" className="btn-ghost" onClick={() => removeItem(idx)} style={{ color: "#ef4444", fontSize: "0.85rem" }}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn-ghost" onClick={addItem} style={{ alignSelf: "flex-start" }}>+ Add Q&amp;A</button>
      <div className="form-group" style={{ marginTop: "0.25rem" }}>
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={(e) => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function ServicesEditor({ content, onChange, packages }: { content: BlockContent; onChange: (c: BlockContent) => void; packages?: import("../../lib/api").Package[] }) {
  const items = content.services_items ?? [];
  function addItem() {
    onChange({ ...content, services_items: [...items, { ID: crypto.randomUUID(), Name: "", Price: "", Description: "", SortOrder: items.length }] });
  }
  function removeItem(idx: number) {
    onChange({ ...content, services_items: items.filter((_, i) => i !== idx) });
  }
  function updateItem(idx: number, field: "Name" | "Price" | "Description", val: string) {
    onChange({ ...content, services_items: items.map((item, i) => i === idx ? { ...item, [field]: val } : item) });
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {items.map((item, idx) => (
        <div key={item.ID} style={{ border: "1px solid var(--border)", borderRadius: "0.5rem", padding: "0.75rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Name</label>
              <input value={item.Name} onChange={(e) => updateItem(idx, "Name", e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Price</label>
              <input value={item.Price} onChange={(e) => updateItem(idx, "Price", e.target.value)} placeholder="€2000" />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: "0.5rem" }}>
            <label>Description</label>
            <input value={item.Description} onChange={(e) => updateItem(idx, "Description", e.target.value)} />
          </div>
          <button type="button" className="btn-ghost" onClick={() => removeItem(idx)} style={{ color: "#ef4444", fontSize: "0.85rem" }}>Remove</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" className="btn-ghost" onClick={addItem} style={{ fontSize: "0.875rem" }}>+ Add service</button>
        {packages && packages.length > 0 && packages.map(p => (
          <button key={p.ID} type="button" className="btn-ghost" style={{ fontSize: "0.78rem", padding: "0.2rem 0.5rem" }}
            onClick={() => onChange({ ...content, services_items: [...items, { ID: crypto.randomUUID(), Name: p.Name, Price: p.PriceCents ? `€${(p.PriceCents / 100).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}` : "", Description: p.Description, SortOrder: items.length }] })}>
            + {p.Name}
          </button>
        ))}
      </div>
      <div className="form-group" style={{ marginTop: "0.25rem" }}>
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={(e) => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function GalleryRowEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  const items = content.items ?? [];
  const [pickerIdx, setPickerIdx] = useState<number | null>(null);

  function addItem() {
    onChange({ ...content, items: [...items, { ID: crypto.randomUUID(), AssetID: "", SortOrder: items.length }] });
  }
  function removeItem(idx: number) {
    onChange({ ...content, items: items.filter((_, i) => i !== idx) });
  }
  function updateItem(idx: number, assetID: string) {
    onChange({ ...content, items: items.map((item, i) => i === idx ? { ...item, AssetID: assetID } : item) });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="form-group">
        <label>Columns</label>
        <div style={{ display: "flex", gap: "1rem" }}>
          {([2, 3, 4] as const).map((n) => (
            <label key={n} style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
              <input type="radio" name="columns" value={n} checked={(content.columns ?? 3) === n} onChange={() => onChange({ ...content, columns: n })} style={{ width: "auto" }} />
              {n}
            </label>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {items.map((item, idx) => (
          <div key={item.ID} style={{ display: "flex", gap: "0.5rem", alignItems: "center", background: "var(--card-bg)", borderRadius: 6, padding: "0.4rem 0.6rem", border: "1px solid var(--border)" }}>
            <span style={{ fontSize: ".75rem", color: "var(--muted)", minWidth: 20 }}>{idx + 1}</span>
            {item.AssetID && _thumbCache[item.AssetID] && (
              <img src={_thumbCache[item.AssetID]} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 3, flexShrink: 0 }} />
            )}
            <button type="button" className="btn-ghost" onClick={() => setPickerIdx(idx)} style={{ fontSize: ".8rem", whiteSpace: "nowrap" }}>
              {item.AssetID ? "Change" : "+ Choose"}
            </button>
            {!item.AssetID && <span style={{ flex: 1, fontSize: ".75rem", color: "var(--muted)" }}>No image selected</span>}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-ghost" onClick={() => removeItem(idx)} style={{ color: "#ef4444", padding: ".1rem .4rem" }}>×</button>
          </div>
        ))}
      </div>
      <button type="button" className="btn-ghost" onClick={addItem} style={{ alignSelf: "flex-start" }}>+ Add image</button>

      <AssetPicker
        open={pickerIdx !== null}
        onClose={() => setPickerIdx(null)}
        onSelect={(webKey) => {
          if (pickerIdx !== null) updateItem(pickerIdx, webKey);
          setPickerIdx(null);
        }}
      />
    </div>
  );
}

function DividerEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  return (
    <div className="form-group">
      <label>Style</label>
      <div style={{ display: "flex", gap: "1rem" }}>
        {(["line", "space"] as const).map((s) => (
          <label key={s} style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
            <input type="radio" name="divider-style" value={s} checked={(content.style ?? "line") === s} onChange={() => onChange({ ...content, style: s })} style={{ width: "auto" }} />
            {s}
          </label>
        ))}
      </div>
    </div>
  );
}

function VideoEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div className="form-group">
        <label>Video URL (embed URL)</label>
        <input value={content.url ?? ""} onChange={(e) => onChange({ ...content, url: e.target.value })} placeholder="https://www.youtube.com/embed/..." />
      </div>
      <div className="form-group">
        <label>Caption</label>
        <input value={content.caption ?? ""} onChange={(e) => onChange({ ...content, caption: e.target.value })} />
      </div>
    </div>
  );
}

function FullBleedEditor({ content, onChange, openPicker }: { content: BlockContent; onChange: (c: BlockContent) => void; openPicker: (field: keyof BlockContent) => void }) {
  function saveField(key: keyof BlockContent, value: string | number) {
    onChange({ ...content, [key]: value });
  }
  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <div className="form-group"><label>Heading</label><input value={content.heading ?? ""} onChange={e => saveField("heading", e.target.value)} /></div>
      <div className="form-group"><label>Subtext</label><input value={content.subtext ?? ""} onChange={e => saveField("subtext", e.target.value)} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem" }}>
        <div className="form-group">
          <label>Height</label>
          <select value={content.height ?? "full"} onChange={e => saveField("height", e.target.value)}>
            <option value="full">Full screen (100vh)</option>
            <option value="tall">Tall (75vh)</option>
            <option value="medium">Medium (50vh)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Text position</label>
          <select value={content.text_align ?? "center"} onChange={e => saveField("text_align", e.target.value)}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
        <div className="form-group">
          <label>Overlay darkness</label>
          <input type="range" min="0" max="0.8" step="0.1" value={content.overlay ?? 0.4} onChange={e => saveField("overlay", parseFloat(e.target.value))} />
          <small style={{ color: "var(--muted)" }}>{((content.overlay ?? 0.4) * 100).toFixed(0)}%</small>
        </div>
      </div>
      <AssetField
        label="Background image"
        value={content.asset_id}
        onOpen={() => openPicker("asset_id")}
        onClear={() => saveField("asset_id", "")}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem" }}>
        <div className="form-group"><label>CTA button text</label><input value={content.cta_label ?? ""} onChange={e => saveField("cta_label", e.target.value)} /></div>
        <LinkPicker label="CTA link" value={content.cta_url ?? ""} onChange={(url) => saveField("cta_url", url)} />
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={e => saveField("font_color", e.target.value)} placeholder="e.g. #ffffff or var(--text)" />
      </div>
    </div>
  );
}

function SplitEditor({ content, onChange, openPicker }: { content: BlockContent; onChange: (c: BlockContent) => void; openPicker: (field: keyof BlockContent) => void }) {
  function saveField(key: keyof BlockContent, value: string) {
    onChange({ ...content, [key]: value });
  }
  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <div className="form-group">
        <label>Image side</label>
        <select value={content.image_side ?? "left"} onChange={e => saveField("image_side", e.target.value)}>
          <option value="left">Image on left</option>
          <option value="right">Image on right</option>
        </select>
      </div>
      <AssetField
        label="Image"
        value={content.asset_id}
        onOpen={() => openPicker("asset_id")}
        onClear={() => saveField("asset_id", "")}
      />
      <div className="form-group"><label>Heading</label><input value={content.heading ?? ""} onChange={e => saveField("heading", e.target.value)} /></div>
      <div className="form-group"><label>Body text (markdown)</label><textarea rows={4} value={content.body ?? ""} onChange={e => saveField("body", e.target.value)} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem" }}>
        <div className="form-group"><label>CTA text</label><input value={content.cta_label ?? ""} onChange={e => saveField("cta_label", e.target.value)} /></div>
        <LinkPicker label="CTA link" value={content.cta_url ?? ""} onChange={(url) => saveField("cta_url", url)} />
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={e => saveField("font_color", e.target.value)} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function StatementEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  function save(key: keyof BlockContent, value: string) {
    onChange({ ...content, [key]: value });
  }
  return (
    <div style={{ display: "grid", gap: ".75rem" }}>
      <div className="form-group">
        <label>Category label <small style={{ color: "var(--muted)", fontWeight: 400 }}>(small accent text above)</small></label>
        <input value={content.label ?? ""} onChange={e => save("label", e.target.value)} placeholder="Over mij / Tarieven / Werkwijze" />
      </div>
      <div className="form-group">
        <label>Statement heading</label>
        <textarea rows={3} value={content.heading ?? ""} onChange={e => save("heading", e.target.value)} placeholder="Large bold opening statement..." />
      </div>
      <div className="form-group">
        <label>Alignment</label>
        <select value={content.align ?? "left"} onChange={e => save("align", e.target.value)}>
          <option value="left">Left</option>
          <option value="center">Center</option>
        </select>
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={e => save("font_color", e.target.value)} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

function SectionEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  function save(key: keyof BlockContent, value: string) {
    onChange({ ...content, [key]: value });
  }
  return (
    <div style={{ display: "grid", gap: ".75rem" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: ".75rem" }}>
        <div className="form-group">
          <label>Direction</label>
          <select value={content.direction ?? "column"} onChange={e => save("direction", e.target.value)}>
            <option value="column">Stack vertically (column)</option>
            <option value="row">Side by side (row)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Gap between blocks</label>
          <select value={content.gap ?? "md"} onChange={e => save("gap", e.target.value)}>
            <option value="none">None</option>
            <option value="sm">Small (0.5rem)</option>
            <option value="md">Medium (1rem)</option>
            <option value="lg">Large (2rem)</option>
          </select>
        </div>
        <div className="form-group">
          <label>Alignment (cross axis)</label>
          <select value={content.align ?? "stretch"} onChange={e => save("align", e.target.value)}>
            <option value="start">Start</option>
            <option value="center">Center</option>
            <option value="end">End</option>
            <option value="stretch">Stretch</option>
          </select>
        </div>
        <div className="form-group">
          <label>Justify (main axis)</label>
          <select value={content.justify ?? "start"} onChange={e => save("justify", e.target.value)}>
            <option value="start">Start</option>
            <option value="center">Center</option>
            <option value="end">End</option>
            <option value="between">Space between</option>
          </select>
        </div>
        <div className="form-group">
          <label>Padding</label>
          <select value={content.padding ?? "none"} onChange={e => save("padding", e.target.value)}>
            <option value="none">None</option>
            <option value="sm">Small (0.75rem)</option>
            <option value="md">Medium (1.5rem)</option>
            <option value="lg">Large (3rem)</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <label>Background color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional CSS color or var(--...))</small></label>
        <input
          value={content.bg_color ?? ""}
          onChange={e => save("bg_color", e.target.value)}
          placeholder="e.g. #f8fafc or var(--card)"
        />
      </div>
    </div>
  );
}

function ProcessEditor({ content, onChange }: { content: BlockContent; onChange: (c: BlockContent) => void }) {
  const steps: Array<{ id: string; heading: string; body: string; sort_order: number }> = content.process_steps ?? [];
  function saveSteps(next: typeof steps) {
    onChange({ ...content, process_steps: next });
  }
  return (
    <div style={{ display: "grid", gap: ".75rem" }}>
      <div className="form-group">
        <label>Intro text</label>
        <input
          value={content.intro ?? ""}
          onChange={e => onChange({ ...content, intro: e.target.value })}
          placeholder="From first contact to delivery..."
        />
      </div>
      <div>
        <label style={{ display: "block", marginBottom: ".5rem" }}>Steps</label>
        {steps.map((step, i) => (
          <div key={step.id || i} style={{ display: "grid", gap: ".5rem", marginBottom: "1rem", paddingLeft: "1rem", borderLeft: "2px solid var(--border)" }}>
            <input
              placeholder={`Step ${i + 1} title`}
              value={step.heading}
              onChange={e => { const s = [...steps]; s[i] = { ...s[i], heading: e.target.value }; saveSteps(s); }}
            />
            <textarea
              rows={2}
              placeholder="Step description..."
              value={step.body}
              onChange={e => { const s = [...steps]; s[i] = { ...s[i], body: e.target.value }; saveSteps(s); }}
            />
            <button
              type="button"
              className="btn-ghost"
              onClick={() => saveSteps(steps.filter((_, j) => j !== i))}
              style={{ fontSize: ".75rem", padding: ".2rem .5rem", color: "#ef4444", width: "fit-content" }}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-ghost"
          onClick={() => saveSteps([...steps, { id: crypto.randomUUID(), heading: "", body: "", sort_order: steps.length }])}
          style={{ fontSize: ".875rem" }}
        >
          + Add step
        </button>
      </div>
      <div className="form-group">
        <label>Font color <small style={{ color: "var(--muted)", fontWeight: 400 }}>(optional, overrides theme)</small></label>
        <input value={content.font_color ?? ""} onChange={e => onChange({ ...content, font_color: e.target.value })} placeholder="e.g. #1a1a1a or var(--text)" />
      </div>
    </div>
  );
}

export function BlockEditor({
  block,
  onSave,
  saving,
  packages,
}: {
  block: Block;
  onSave: (content: BlockContent) => Promise<void>;
  saving: boolean;
  packages?: import("../../lib/api").Package[];
}) {
  const [content, setContent] = useState<BlockContent>(block.Content);
  const [error, setError] = useState<string | null>(null);
  // Asset picker state — lives here so each block editor has its own picker
  const [pickerField, setPickerField] = useState<keyof BlockContent | null>(null);

  async function handleSave() {
    setError(null);
    try {
      await onSave(content);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  function openPicker(field: keyof BlockContent) {
    setPickerField(field);
  }

  function handlePickerSelect(webKey: string) {
    if (pickerField) {
      setContent((c) => ({ ...c, [pickerField]: webKey }));
    }
    setPickerField(null);
  }

  const editorProps = { content, onChange: setContent, openPicker };

  return (
    <div style={{ padding: "1rem", background: "var(--card)", borderRadius: "0 0 0.5rem 0.5rem", borderTop: "1px solid var(--border)" }}>
      {block.BlockType === "hero" && <HeroEditor {...editorProps} />}
      {block.BlockType === "full_bleed" && <FullBleedEditor {...editorProps} />}
      {block.BlockType === "split" && <SplitEditor {...editorProps} />}
      {block.BlockType === "text" && <TextEditor content={content} onChange={setContent} />}
      {block.BlockType === "image" && <ImageEditor {...editorProps} />}
      {block.BlockType === "quote" && <QuoteEditor content={content} onChange={setContent} />}
      {block.BlockType === "callout" && <CalloutEditor content={content} onChange={setContent} />}
      {block.BlockType === "faq" && <FAQEditor content={content} onChange={setContent} />}
      {block.BlockType === "services" && <ServicesEditor content={content} onChange={setContent} packages={packages} />}
      {block.BlockType === "gallery_row" && <GalleryRowEditor content={content} onChange={setContent} />}
      {block.BlockType === "divider" && <DividerEditor content={content} onChange={setContent} />}
      {block.BlockType === "video" && <VideoEditor content={content} onChange={setContent} />}
      {block.BlockType === "statement" && <StatementEditor content={content} onChange={setContent} />}
      {block.BlockType === "process" && <ProcessEditor content={content} onChange={setContent} />}
      {block.BlockType === "section" && <SectionEditor content={content} onChange={setContent} />}
      {error && <div className="alert alert-error" style={{ marginTop: "0.75rem" }}>{error}</div>}
      <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ marginTop: "1rem" }}>
        {saving ? "Saving…" : "Save block"}
      </button>
      <AssetPicker
        open={pickerField !== null}
        onClose={() => setPickerField(null)}
        onSelect={handlePickerSelect}
      />
    </div>
  );
}
